
import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import type { SelectedCriterion, DynamicRatingData } from '@/types/evaluationCriteria';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';
import { BrandingSettings } from '@/hooks/useReferentBranding';
import { BrandedSlider, BrandedBackground, BrandedText } from '@/components/shared/ui';

interface DynamicRatingStepProps {
  criteria: SelectedCriterion[];
  initialData: DynamicRatingData;
  updateFormData: (data: any) => void;
  branding?: BrandingSettings | null;
}

export const DynamicRatingStep = ({ criteria, initialData, updateFormData, branding }: DynamicRatingStepProps) => {
  const [ratings, setRatings] = useState<DynamicRatingData>(initialData);
  const colors = useReferentBrandingColors(branding);

  const updateRating = (criterionId: string, value: number) => {
    setRatings(prev => ({ ...prev, [criterionId]: value }));
  };

  // Mettre à jour formData quand les ratings changent
  useEffect(() => {
    updateFormData({ ratings });
  }, [ratings, updateFormData]);

  const averageScore = criteria.length > 0 
    ? Object.values(ratings).reduce((sum, score) => sum + score, 0) / criteria.length 
    : 0;

  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      {/* Description sans titre (titre géré par le container) */}
      <div className="text-center">
        <p className="text-gray-600">
          Merci d'évaluer le candidat sur les critères suivants (de 1 à 10).
        </p>
      </div>

      <div className="space-y-8">
        {criteria.map((criterion) => (
          <div key={criterion.id} className="space-y-4">
            <div>
              <Label className="text-base font-medium">{criterion.name}</Label>
              <p className="text-sm text-gray-600 mt-1">{criterion.description}</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Faible (1)</span>
                <BrandedText 
                  className="text-2xl font-bold"
                  branding={branding}
                >
                  {ratings[criterion.id] || 5}/10
                </BrandedText>
                <span className="text-sm text-gray-500">Excellent (10)</span>
              </div>
              
              <BrandedSlider
                min={1}
                max={10}
                step={1}
                value={[ratings[criterion.id] || 5]}
                onValueChange={(value) => updateRating(criterion.id, value[0])}
                branding={branding}
              />
              
              <div className="flex justify-between text-xs text-gray-400">
                {Array.from({ length: 10 }, (_, i) => (
                  <span key={i + 1}>{i + 1}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <BrandedBackground
        branding={branding}
        opacity={0.1}
        withBorder={true}
        className="p-4 rounded-lg"
      >
        <BrandedText 
          className="font-medium mb-2"
          branding={branding}
          as="h4"
        >
          Récapitulatif de vos évaluations
        </BrandedText>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {criteria.map((criterion) => (
            <div key={criterion.id} className="flex justify-between">
              <span className="text-gray-700 truncate mr-2">{criterion.name}:</span>
              <BrandedText 
                className="font-medium"
                branding={branding}
              >
                {ratings[criterion.id] || 5}/10
              </BrandedText>
            </div>
          ))}
        </div>
        <div 
          className="mt-3 pt-3 border-t"
          style={{ borderColor: colors.primaryWithOpacity(0.3) }}
        >
          <div className="flex justify-between font-medium">
            <span className="text-gray-700">Moyenne générale:</span>
            <BrandedText branding={branding}>
              {averageScore.toFixed(1)}/10
            </BrandedText>
          </div>
        </div>
      </BrandedBackground>
    </div>
  );
};
